import React, { useEffect, useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  InformationCircleIcon,
  ArrowPathIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import Modal from "../componentLibrary/Modal";
import Button from "../componentLibrary/Button";
import Carousel from "../componentLibrary/Carousel";
import { useNotification } from "../hooks/useNotification";
import { AnnouncementI, AnnouncementTypeEnum } from "../types";
import {
  useTrackAnnouncementView,
  useDismissAnnouncement,
  useTrackAnnouncementAction,
} from "../api/mutations";

interface AnnouncementModalProps {
  open: boolean;
  announcements: AnnouncementI[];
  onClose: () => void;
  /** When true, no tracking or dismiss mutations; close only */
  previewMode?: boolean;
}

const sanitizeHTML = (html: string): string => {
  const div = document.createElement("div");
  div.textContent = html;
  return div.innerHTML;
};

const isSafeHref = (href: string): boolean => {
  if (!href || typeof href !== "string" || !href.trim()) return false;
  try {
    const urlObj = new URL(href, window.location.origin);
    return ["http:", "https:"].includes(urlObj.protocol);
  } catch {
    return false;
  }
};

const convertMarkdownLinks = (message: string): string => {
  const linkRegex = /\$([^$]+)\$([^$]+)\$/g;
  return message.replace(linkRegex, (_, text, url) => {
    try {
      const urlObj = new URL(url, window.location.origin);
      if (!["http:", "https:"].includes(urlObj.protocol)) {
        return text;
      }
      return `<a href="${sanitizeHTML(url)}" class="text-blue-500 hover:text-blue-600 underline cursor-pointer announcement-link" data-url="${sanitizeHTML(url)}">${sanitizeHTML(text)}</a>`;
    } catch {
      return text;
    }
  });
};

const stripUnsafeHrefs = (html: string): string => {
  const div = document.createElement("div");
  div.innerHTML = html;
  div.querySelectorAll("a[href]").forEach((a) => {
    const href = a.getAttribute("href");
    if (href && !isSafeHref(href)) {
      const span = document.createElement("span");
      span.textContent = a.textContent || "";
      a.parentNode?.replaceChild(span, a);
    }
  });
  return div.innerHTML;
};

const looksLikeHtml = (message: string): boolean =>
  /^\s*</.test(message) || message.includes("</");

const formatMessageForDisplay = (message: string): string =>
  looksLikeHtml(message)
    ? stripUnsafeHrefs(message)
    : `<p>${convertMarkdownLinks(sanitizeHTML(message))}</p>`;

const getIcon = (type: AnnouncementTypeEnum) => {
  const iconClass = "h-10 w-10 flex-shrink-0";
  const iconMap = {
    [AnnouncementTypeEnum.UPDATE]: (
      <ArrowPathIcon className={`${iconClass} text-blue-500`} />
    ),
    [AnnouncementTypeEnum.FEATURE]: (
      <SparklesIcon className={`${iconClass} text-green-500`} />
    ),
    [AnnouncementTypeEnum.INFO]: (
      <InformationCircleIcon className={`${iconClass} text-blue-500`} />
    ),
  };

  return iconMap[type] || iconMap[AnnouncementTypeEnum.INFO];
};

export default function AnnouncementModal({
  open,
  announcements,
  onClose,
  previewMode = false,
}: AnnouncementModalProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [viewedAnnouncements, setViewedAnnouncements] = useState<Set<string>>(
    new Set(),
  );
  const [isProcessing, setIsProcessing] = useState(false);

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { notify } = useNotification();

  const currentAnnouncement = announcements[currentIndex];
  const isLastAnnouncement = currentIndex === announcements.length - 1;

  const trackViewMutation = useTrackAnnouncementView({
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["announcements", "interactions"],
      });
    },
  });

  const dismissMutation = useDismissAnnouncement({
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["announcements"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["announcements", "interactions"],
      });
    },
  });

  const trackActionMutation = useTrackAnnouncementAction({
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["announcements", "interactions"],
      });
    },
  });

  const handleNavigation = useCallback(
    (url: string) => {
      try {
        const urlObj = new URL(url, window.location.origin);

        if (urlObj.origin !== window.location.origin) {
          window.open(url, "_blank", "noopener,noreferrer");
        } else {
          navigate(urlObj.pathname + urlObj.search + urlObj.hash);
        }
      } catch {
        const internalPath = url.startsWith("/") ? url : `/${url}`;
        navigate(internalPath);
      }
    },
    [navigate],
  );

  useEffect(() => {
    if (
      previewMode ||
      !open ||
      !currentAnnouncement ||
      viewedAnnouncements.has(currentAnnouncement._id)
    ) {
      return;
    }
    trackViewMutation.mutate(currentAnnouncement._id);
    setViewedAnnouncements((prev) =>
      new Set(prev).add(currentAnnouncement._id),
    );
  }, [
    currentAnnouncement?._id,
    open,
    previewMode,
    trackViewMutation,
    viewedAnnouncements,
  ]);

  useEffect(() => {
    const handleLinkClick = async (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest?.("a");
      if (!anchor || !anchor.getAttribute("href")) return;
      if (!anchor.closest(".announcement-message-body")) return;

      const href = anchor.getAttribute("href");
      if (!href || !currentAnnouncement || isProcessing) return;

      e.preventDefault();
      e.stopPropagation();
      if (previewMode) {
        onClose();
        return;
      }
      if (!isSafeHref(href)) return;

      setIsProcessing(true);
      try {
        await trackActionMutation.mutateAsync({
          announcementId: currentAnnouncement._id,
          actionLabel: `Link: ${href}`,
        });

        await dismissMutation.mutateAsync(currentAnnouncement._id);

        onClose();
        handleNavigation(href);
      } catch (error) {
        notify("error", "Failed to navigate. Please try again.");
      } finally {
        setIsProcessing(false);
      }
    };

    if (open && currentAnnouncement) {
      document.addEventListener("click", handleLinkClick);
      return () => document.removeEventListener("click", handleLinkClick);
    }
  }, [
    currentAnnouncement,
    open,
    isProcessing,
    previewMode,
    trackActionMutation,
    dismissMutation,
    onClose,
    handleNavigation,
    notify,
  ]);

  const handleSlideChange = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  const handleClose = async () => {
    if (isProcessing) return;

    if (previewMode) {
      onClose();
      return;
    }

    setIsProcessing(true);

    try {
      for (const announcement of announcements) {
        await dismissMutation.mutateAsync(announcement._id);
      }

      await queryClient.invalidateQueries({
        queryKey: ["announcements"],
      });

      onClose();
    } catch (error) {
      notify("error", "Failed to close announcements. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSkipAll = async () => {
    if (isProcessing) return;

    if (previewMode) {
      onClose();
      return;
    }

    setIsProcessing(true);

    try {
      await Promise.allSettled(
        announcements.map((announcement) =>
          trackActionMutation.mutateAsync({
            announcementId: announcement._id,
            actionLabel: "Skip All",
          }),
        ),
      );

      await Promise.allSettled(
        announcements.map((announcement) =>
          dismissMutation.mutateAsync(announcement._id),
        ),
      );

      await queryClient.invalidateQueries({
        queryKey: ["announcements"],
      });

      onClose();
    } catch (error) {
      notify("error", "Failed to skip announcements. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleActionClick = async (actionLabel: string, actionUrl?: string) => {
    if (isProcessing) return;

    if (previewMode) {
      onClose();
      return;
    }

    setIsProcessing(true);

    try {
      await trackActionMutation.mutateAsync({
        announcementId: currentAnnouncement._id,
        actionLabel,
      });

      await dismissMutation.mutateAsync(currentAnnouncement._id);

      if (actionUrl && isSafeHref(actionUrl)) {
        onClose();
        handleNavigation(actionUrl);
        return;
      }

      if (isLastAnnouncement) {
        onClose();
      } else {
        setCurrentIndex((prev) => prev + 1);
        setIsProcessing(false);
      }
    } catch (error) {
      notify("error", "Failed to process action. Please try again.");
      setIsProcessing(false);
    }
  };

  const renderAnnouncementContent = (announcement: AnnouncementI) => {
    const displayMessage = formatMessageForDisplay(announcement.message);
    const hasActions = announcement.actions && announcement.actions.length > 0;

    return (
      <div className="w-full flex flex-col">
        <div
          className={`ql-editor rounded-md px-4 pt-4 text-gray-600 text-sm md:text-base break-words leading-relaxed announcement-message-body ${hasActions ? "mb-4" : ""}`}
          style={{
            wordWrap: "break-word",
            overflowWrap: "break-word",
            hyphens: "auto",
          }}
          dangerouslySetInnerHTML={{ __html: displayMessage }}
        />

        {hasActions && (
          <div className="flex gap-2 md:gap-3 justify-center flex-wrap mt-2">
            {announcement.actions?.map((action, index) => (
              <Button
                key={`${announcement._id}-action-${index}`}
                variant={
                  action.variant === "secondary" ? "secondary" : "primary"
                }
                onClick={() => {
                  handleActionClick(action.label, action.url);
                }}
                disabled={isProcessing}
                loading={isProcessing}
              >
                {action.label}
              </Button>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (!currentAnnouncement) {
    return null;
  }

  return (
    <Modal
      isOpen={open}
      onCloseModal={() => {}}
      size="lg"
      height="auto"
      maxHeight="85vh"
      isCloseButtonVisible={false}
      title={
        <div className="flex items-center justify-start gap-4">
          {getIcon(currentAnnouncement.type)}
          <span className="text-base md:text-xl font-bold">
            {currentAnnouncement.title}
          </span>
        </div>
      }
      footer={
        <div className="flex justify-center gap-3 py-2">
          {isLastAnnouncement ? (
            <Button
              onClick={handleClose}
              loading={isProcessing}
              disabled={isProcessing}
            >
              Close
            </Button>
          ) : (
            announcements.length > 1 && (
              <Button
                onClick={handleSkipAll}
                variant="secondary"
                disabled={isProcessing}
                loading={isProcessing}
              >
                Skip All
              </Button>
            )
          )}
        </div>
      }
    >
      <div className="w-full">
        {announcements.length > 1 ? (
          <div className="min-h-[200px] max-h-[65vh] overflow-hidden">
            <Carousel
              showButtonsDown={true}
              paginationType="dot"
              onSlideChange={handleSlideChange}
            >
              {announcements.map((announcement) => (
                <div key={announcement._id} className="w-full px-2 py-3">
                  {renderAnnouncementContent(announcement)}
                </div>
              ))}
            </Carousel>
          </div>
        ) : (
          <div className="w-full px-2 py-3">
            {renderAnnouncementContent(currentAnnouncement)}
          </div>
        )}
      </div>
    </Modal>
  );
}
